import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { RuleDeadline, StateRequirement } from '../../lib/types';
import { Sheet } from '../../ui/Sheet';
import { ruleText } from '../Compliance';
import { useParent } from '../ParentContext';

/**
 * Admin → Compliance (#599): each state's homeschool requirements, as families see them in School Settings. Kept
 * here so a law change is an edit, not an update. Rows not reviewed for a year are flagged.
 */
interface Data {
  settings: { enabled: boolean; behindDays: number; coreSubjects: string[]; autoSplit?: boolean; deadlines: DeadlineSettings };
  requirements: StateRequirement[];
}
/** Deadline reminders and documents (#609). */
interface DeadlineSettings {
  reminders: boolean;
  reminderDays: number;
  dayBefore: boolean;
  documents: boolean;
}
const EMPTY: StateRequirement = {
  id: '',
  state: '',
  stateName: '',
  option: '',
  days: null,
  hours: null,
  hoursPerDay: null,
  coreHours: null,
  either: false,
  perDay: 'minimum',
  notes: '',
  sourceUrl: '',
  checkUrl: '',
  reviewedAt: new Date().toISOString().slice(0, 10),
  deadlines: [],
};
const stale = (ymd: string) => !ymd || Date.now() - new Date(`${ymd}T12:00:00`).getTime() > 365 * 86_400_000;

export function AdminCompliance() {
  const { toast } = useParent();
  const [data, setData] = useState<Data | null>(null);
  const [editing, setEditing] = useState<StateRequirement | null>(null);
  const load = useCallback(() => {
    api.get<Data>('/api/admin/compliance').then(setData).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);
  if (!data) return <p className="muted">Loading…</p>;

  async function saveSettings(patch: Partial<Omit<Data['settings'], 'deadlines'>> & { deadlines?: Partial<DeadlineSettings> }) {
    try {
      const settings = await api.put<Data['settings']>('/api/admin/compliance/settings', patch);
      setData((d) => (d ? { ...d, settings } : d));
      toast({ text: 'Saved.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  async function remove(r: StateRequirement) {
    if (!confirm(`Remove ${r.stateName}: ${r.option}?${r.families ? `\n\n${r.families} famil${r.families === 1 ? 'y follows' : 'ies follow'} it; they’ll stop tracking until they pick another.` : ''}`)) return;
    try {
      await api.del(`/api/admin/compliance/requirements/${r.id}`);
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <div className="stack">
      <section className="card stack">
        <div className="row-between wrap">
          <h2>State requirements</h2>
          <button className="btn" onClick={() => setEditing(EMPTY)}>
            <Plus size={16} /> Add a state or option
          </button>
        </div>
        <p className="muted small">
          Families pick their state and how they homeschool in Settings → School Settings; each child’s Attendance tab then shows their days and hours against it. Change a
          row when a law changes. Rows not reviewed in a year are flagged. Families are told this isn’t legal advice.
        </p>
        <label className="check">
          <input type="checkbox" checked={data.settings.enabled} onChange={(e) => saveSettings({ enabled: e.target.checked })} />
          <span>
            <strong>Offer state requirement tracking</strong>
            <span className="muted small block">Off: families don’t see the state picker, the progress card or the weekly “falling behind” note.</span>
          </span>
        </label>
        <label>
          Behind after (school days)
          <input type="number" className="compliance-behind" min={1} max={60} defaultValue={data.settings.behindDays} onBlur={(e) => Number(e.target.value) !== data.settings.behindDays && saveSettings({ behindDays: Number(e.target.value) })} />
          <span className="muted small">How far behind the expected pace a child must be before they’re shown as behind and the weekly note goes out.</span>
        </label>
        <label>
          Core subjects <span className="muted small">(one per line)</span>
          <textarea
            rows={4}
            defaultValue={(data.settings.coreSubjects ?? []).join('\n')}
            onBlur={(e) => {
              const next = e.target.value.split(/\n|,/).map((x) => x.trim()).filter(Boolean);
              if (next.join('|') !== (data.settings.coreSubjects ?? []).join('|')) void saveSettings({ coreSubjects: next });
            }}
          />
          <span className="muted small">
            Subjects counted as core where a state counts core hours (Missouri: 600 of 1,000), for families who haven’t chosen their own in a child’s Profile → Subjects.
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={data.settings.autoSplit !== false} onChange={(e) => saveSettings({ autoSplit: e.target.checked })} />
          <span>
            <strong>Split attendance from the planner by itself</strong>
            <span className="muted small block">
              A day with hours and no split of its own is split by subject from that day’s planner and lesson plans, and counts toward time by subject
              and core hours. A parent’s own split always wins. Off: days stay unsplit until a parent splits them.
            </span>
          </span>
        </label>
        <h3>Deadlines</h3>
        <p className="muted small">
          Each rule’s filing deadlines (edit a rule below) show on each child’s Attendance tab, where families mark them filed. Families can turn the reminders off in
          Settings → Notifications.
        </p>
        <label className="check">
          <input type="checkbox" checked={data.settings.deadlines.reminders} onChange={(e) => saveSettings({ deadlines: { reminders: e.target.checked } })} />
          <span>Remind families ahead of each deadline, by push and email</span>
        </label>
        <label>
          Days ahead
          <input
            type="number"
            className="compliance-behind"
            min={1}
            max={60}
            defaultValue={data.settings.deadlines.reminderDays}
            disabled={!data.settings.deadlines.reminders}
            onBlur={(e) => Number(e.target.value) !== data.settings.deadlines.reminderDays && saveSettings({ deadlines: { reminderDays: Number(e.target.value) } })}
          />
        </label>
        <label className="check">
          <input type="checkbox" checked={data.settings.deadlines.dayBefore} disabled={!data.settings.deadlines.reminders} onChange={(e) => saveSettings({ deadlines: { dayBefore: e.target.checked } })} />
          <span>Again the day before, if it isn’t marked filed</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={data.settings.deadlines.documents} onChange={(e) => saveSettings({ deadlines: { documents: e.target.checked } })} />
          <span>
            <strong>Documents</strong>
            <span className="muted small block">Families can make the notice, instruction plan (IHIP) and progress reports from their records.</span>
          </span>
        </label>
      </section>

      <section className="card">
        <div className="table-scroll">
          <table className="compliance-table">
            <thead>
              <tr>
                <th>State</th>
                <th>Option</th>
                <th>Requirement</th>
                <th>Deadlines</th>
                <th>Last reviewed</th>
                <th>Families</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.requirements.map((r) => (
                <tr key={r.id}>
                  <td>{r.stateName}</td>
                  <td>{r.option}</td>
                  <td>{ruleText(r)}</td>
                  <td>{r.deadlines?.length || '—'}</td>
                  <td className={stale(r.reviewedAt) ? 'warn-text' : ''}>{r.reviewedAt || 'Never'}</td>
                  <td>{r.families ?? 0}</td>
                  <td className="nowrap">
                    <div className="row-gap">
                      <button className="icon-btn" onClick={() => setEditing(r)} aria-label={`Edit ${r.stateName}: ${r.option}`}>
                        <Pencil size={15} />
                      </button>
                      <button className="icon-btn" onClick={() => remove(r)} aria-label={`Remove ${r.stateName}: ${r.option}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {editing && (
        <RequirementSheet
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function RequirementSheet({ initial, onClose, onSaved }: { initial: StateRequirement; onClose: () => void; onSaved: () => void }) {
  const { toast } = useParent();
  const [r, setR] = useState(initial);
  const set = <K extends keyof StateRequirement>(k: K, v: StateRequirement[K]) => setR((x) => ({ ...x, [k]: v }));
  const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));
  async function save() {
    const body = { ...r };
    try {
      if (r.id) await api.put(`/api/admin/compliance/requirements/${r.id}`, body);
      else await api.post('/api/admin/compliance/requirements', body);
      toast({ text: 'Saved.', tone: 'good' });
      onSaved();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  return (
    <Sheet title={r.id ? `${initial.stateName}: ${initial.option}` : 'Add a state or option'} onClose={onClose}>
      <div className="stack">
        <div className="field-row">
          <label>
            State code
            <input value={r.state} maxLength={2} onChange={(e) => set('state', e.target.value.toUpperCase())} placeholder="SC" />
          </label>
          <label className="grow">
            State name
            <input value={r.stateName} onChange={(e) => set('stateName', e.target.value)} placeholder="South Carolina" />
          </label>
        </div>
        <label>
          Option
          <input value={r.option} onChange={(e) => set('option', e.target.value)} placeholder="Option 3: through an accountability association" />
        </label>
        <div className="field-row">
          <label>
            Days a year
            <input type="number" min={1} max={366} value={r.days ?? ''} onChange={(e) => set('days', numOrNull(e.target.value))} />
          </label>
          <label>
            Hours a year
            <input type="number" min={1} max={3000} value={r.hours ?? ''} onChange={(e) => set('hours', numOrNull(e.target.value))} />
          </label>
          <label>
            Hours a day
            <input type="number" min={0.5} max={12} step={0.5} value={r.hoursPerDay ?? ''} onChange={(e) => set('hoursPerDay', numOrNull(e.target.value))} />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={r.either} onChange={(e) => set('either', e.target.checked)} />
          <span>Meeting the days OR the hours is enough (e.g. Pennsylvania)</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={r.perDay === 'average'} onChange={(e) => set('perDay', e.target.checked ? 'average' : 'minimum')} />
          <span>Hours a day is an average over the year, not a minimum each day (e.g. Colorado)</span>
        </label>
        <label>
          Core-subject hours <span className="muted small">(shown only, e.g. Missouri’s 600)</span>
          <input type="number" min={1} max={3000} value={r.coreHours ?? ''} onChange={(e) => set('coreHours', numOrNull(e.target.value))} />
        </label>
        <label>
          What else the family has to do
          <textarea rows={4} maxLength={1000} value={r.notes} onChange={(e) => set('notes', e.target.value)} />
        </label>
        <label>
          Source (statute or state education department)
          <input type="url" value={r.sourceUrl} onChange={(e) => set('sourceUrl', e.target.value)} />
        </label>
        <label>
          Cross-check (e.g. HSLDA)
          <input type="url" value={r.checkUrl} onChange={(e) => set('checkUrl', e.target.value)} />
        </label>
        <label>
          Last reviewed
          <input type="date" value={r.reviewedAt} onChange={(e) => set('reviewedAt', e.target.value)} />
        </label>
        <label>
          Required subjects <span className="muted small">(one per line; after “=”, the family subject names it matches on its own)</span>
          <textarea
            rows={10}
            defaultValue={(r.requiredSubjects ?? []).map((s) => (s.aliases.length ? `${s.name} = ${s.aliases.join(', ')}` : s.name)).join('\n')}
            onChange={(e) =>
              set(
                'requiredSubjects',
                e.target.value
                  .split('\n')
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .map((line) => {
                    const [name, rest = ''] = line.split('=');
                    return { name: name!.trim(), aliases: rest.split(',').map((a) => a.trim()).filter(Boolean) };
                  })
                  .filter((s) => s.name),
              )
            }
            placeholder={'Arithmetic = Math\nEnglish (reading, writing, spelling) = Reading, Writing, Spelling'}
          />
        </label>
        <DeadlineEditor list={r.deadlines ?? []} onChange={(list) => set('deadlines', list)} />
        <div className="sheet-actions">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={save} disabled={!r.state || !r.stateName || !r.option}>
            Save
          </button>
        </div>
      </div>
    </Sheet>
  );
}

const DUE = /^(?:(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])|(start|end)[+-]\d{1,3})$/;
const DOCS: { value: RuleDeadline['doc']; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'notice', label: 'Notice of intent' },
  { value: 'ihip', label: 'Instruction plan (IHIP)' },
  { value: 'report', label: 'Progress report' },
];

/** A rule's filing deadlines (#609): name, when it's due, the document ClassGuardian fills in, and whether families set the date. */
function DeadlineEditor({ list, onChange }: { list: RuleDeadline[]; onChange: (list: RuleDeadline[]) => void }) {
  const set = (i: number, patch: Partial<RuleDeadline>) => onChange(list.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  return (
    <div className="stack deadline-editor">
      <span className="field-label">Deadlines</span>
      {list.map((d, i) => (
        <div key={d.id ?? `new-${i}`} className="deadline-edit-row">
          <input className="deadline-edit-title" value={d.title} onChange={(e) => set(i, { title: e.target.value })} maxLength={120} placeholder="Notice of intent" aria-label="Deadline" />
          <button type="button" className="icon-btn" aria-label={`Remove ${d.title || 'this deadline'}`} onClick={() => onChange(list.filter((_, j) => j !== i))}>
            <X size={15} />
          </button>
          <input
            value={d.due}
            onChange={(e) => set(i, { due: e.target.value.trim() })}
            maxLength={10}
            placeholder="07-01"
            aria-label="Due"
            aria-invalid={!DUE.test(d.due)}
            className={DUE.test(d.due) ? '' : 'invalid'}
          />
          <select className="deadline-edit-doc" value={d.doc} onChange={(e) => set(i, { doc: e.target.value as RuleDeadline['doc'] })} aria-label="Document">
            {DOCS.map((x) => (
              <option key={x.value} value={x.value}>
                {x.label}
              </option>
            ))}
          </select>
          <label className="check compact">
            <input type="checkbox" checked={d.familyDate} onChange={(e) => set(i, { familyDate: e.target.checked })} />
            <span>Families can change the date (their plan sets it)</span>
          </label>
        </div>
      ))}
      <button type="button" className="link-btn" onClick={() => onChange([...list, { title: '', due: '', doc: '', familyDate: false }])}>
        <Plus size={14} /> Add a deadline
      </button>
      <span className="muted small">
        Due is a date each school year, like 07-01 (July 1), or days from the family’s school year: start+30, start-14, end+0. Families see them on each child’s Attendance tab.
      </span>
    </div>
  );
}
